import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";
import { toast } from "sonner";
import { PsvReviewStatus } from "@api/shared-runtime/psv-review";
import { useTRPC } from "@/trpc";
import { validateEvidenceUpload } from "@/features/provider-workspace/psv-review/evidence-upload";
import type { ReportItem, RunReport, RunScope } from "../types";
import { uploadWithServerToken } from "@/ajax/upload/server-issued-upload";
import { fileSha256 } from "@/common/utils/file-sha256";

const CONFLICT_CODE = "CONFLICT";
const STALE_ITEM_MESSAGE = "This item changed. Reloading.";
const UNDO_LABEL = "Undo";
const STATUS_TOAST: Record<PsvReviewStatus, string> = {
  [PsvReviewStatus.Pending]: "Moved back to pending",
  [PsvReviewStatus.InReview]: "Marked in review",
  [PsvReviewStatus.Accepted]: "Accepted",
  [PsvReviewStatus.Concern]: "Marked as concern",
};

export type ItemDecisions = ReturnType<typeof useItemDecisions>;

function isConflict(error: unknown): boolean {
  return error instanceof TRPCClientError && error.data?.code === CONFLICT_CODE;
}

/** All staff decisions on a run's items. Accept and Concern update the cached
 * report immediately and are rolled back by a refetch if the server refuses;
 * a key stays busy until its request settles so a second click cannot send a
 * stale revision. */
export function useItemDecisions({
  scope,
  runId,
}: {
  scope: RunScope;
  runId: string;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [busyKeys, setBusyKeys] = useState<string[]>([]);
  const runQueryKey = trpc.psv.getRun.queryKey({ ...scope, runId });
  const mutationScope = {
    organizationBusinessUnitId: scope.organizationBusinessUnitId,
    providerUserId: scope.providerUserId,
    expectedAffiliationId: scope.expectedAffiliationId,
    runId,
  };
  const setStatusMutation = useMutation(
    trpc.psv.review.setStatus.mutationOptions(),
  );
  const saveNoteMutation = useMutation(
    trpc.psv.review.saveNote.mutationOptions(),
  );
  const acceptAllMutation = useMutation(
    trpc.psv.review.acceptAllValid.mutationOptions(),
  );
  const createUploadMutation = useMutation(
    trpc.psv.review.createUpload.mutationOptions(),
  );
  const finalizeUploadMutation = useMutation(
    trpc.psv.review.finalizeUpload.mutationOptions(),
  );
  const withdrawFileMutation = useMutation(
    trpc.psv.review.withdrawFile.mutationOptions(),
  );

  function refresh() {
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: runQueryKey }),
      queryClient.invalidateQueries(trpc.psv.listRuns.pathFilter()),
      queryClient.invalidateQueries(trpc.psv.review.listQueue.pathFilter()),
    ]);
  }

  function patchDecision({
    itemKey,
    patch,
  }: {
    itemKey: string;
    patch: Partial<NonNullable<ReportItem["decision"]>>;
  }) {
    queryClient.setQueryData<RunReport>(runQueryKey, (report) => {
      if (!report) {
        return report;
      }
      return {
        ...report,
        items: report.items.map((item) =>
          item.key === itemKey && item.decision
            ? { ...item, decision: { ...item.decision, ...patch } }
            : item,
        ),
      };
    });
  }

  async function guarded({
    keys,
    action,
  }: {
    keys: string[];
    action: () => Promise<void>;
  }) {
    setBusyKeys((current) => [...current, ...keys]);
    try {
      await action();
    } catch (error) {
      if (isConflict(error)) {
        toast.error(STALE_ITEM_MESSAGE);
      } else {
        toast.error(
          error instanceof Error ? error.message : "Could not save changes.",
        );
      }
    } finally {
      setBusyKeys((current) => current.filter((key) => !keys.includes(key)));
      await refresh();
    }
  }

  /** Sets a status, then offers to restore the previous one. */
  function setStatus({
    item,
    status,
    expectedRevision,
    offerUndo = true,
    reason,
  }: {
    item: ReportItem;
    status: PsvReviewStatus;
    expectedRevision?: number;
    offerUndo?: boolean;
    /** Required by the server to reopen a decision carried forward. */
    reason?: string;
  }) {
    const decision = item.decision;
    if (!decision) {
      return Promise.resolve();
    }
    const previous = decision.status;
    return guarded({
      keys: [item.key],
      action: async () => {
        // Any staff status change ends the carried chain on the server, and a
        // reopen reason becomes the item's note.
        patchDecision({
          itemKey: item.key,
          patch: reason
            ? { status, carriedFrom: null, note: reason }
            : { status, carriedFrom: null },
        });
        const row = await setStatusMutation.mutateAsync({
          ...mutationScope,
          itemKey: item.key,
          expectedRevision: expectedRevision ?? decision.revision,
          status,
          reason,
        });
        patchDecision({ itemKey: item.key, patch: { revision: row.revision } });
        if (offerUndo) {
          toast(STATUS_TOAST[status], {
            action: {
              label: UNDO_LABEL,
              onClick: () =>
                void setStatus({
                  item,
                  status: previous,
                  expectedRevision: row.revision,
                  offerUndo: false,
                }),
            },
          });
        }
      },
    });
  }

  function saveNote({ item, note }: { item: ReportItem; note: string }) {
    const decision = item.decision;
    if (!decision) {
      return Promise.resolve();
    }
    return guarded({
      keys: [item.key],
      action: async () => {
        await saveNoteMutation.mutateAsync({
          ...mutationScope,
          itemKey: item.key,
          expectedRevision: decision.revision,
          note,
        });
        toast("Note saved");
      },
    });
  }

  function acceptAllValid(items: ReportItem[]) {
    const keys = items.map((item) => item.key);
    return guarded({
      keys,
      action: async () => {
        const accepted = await acceptAllMutation.mutateAsync(mutationScope);
        toast(
          `Accepted ${accepted.length} ${accepted.length === 1 ? "item" : "items"}`,
          {
            action: {
              label: UNDO_LABEL,
              onClick: () => void undoAccepted(accepted),
            },
          },
        );
      },
    });
  }

  async function undoAccepted(
    accepted: { itemKey: string; revision: number }[],
  ) {
    await guarded({
      keys: accepted.map((row) => row.itemKey),
      action: async () => {
        for (const row of accepted) {
          await setStatusMutation.mutateAsync({
            ...mutationScope,
            itemKey: row.itemKey,
            expectedRevision: row.revision,
            status: PsvReviewStatus.Pending,
          });
        }
      },
    });
  }

  function uploadFile({ item, file }: { item: ReportItem; file: File }) {
    const decision = item.decision;
    if (!decision) {
      return Promise.resolve();
    }
    return guarded({
      keys: [item.key],
      action: async () => {
        const { mimeType } = validateEvidenceUpload([file]);
        const reservation = await createUploadMutation.mutateAsync({
          ...mutationScope,
          itemKey: item.key,
          expectedRevision: decision.revision,
          fileName: file.name,
          fileSizeBytes: file.size,
          mimeType,
          sha256: await fileSha256(file),
        });
        await uploadWithServerToken({
          upload: {
            bucketName: reservation.bucket,
            storagePath: reservation.path,
            token: reservation.token,
          },
          body: file,
          contentType: mimeType,
        });
        await finalizeUploadMutation.mutateAsync({
          ...mutationScope,
          itemKey: item.key,
          fileId: reservation.fileId,
        });
        toast("Evidence uploaded");
      },
    });
  }

  function withdrawFile({
    item,
    fileId,
  }: {
    item: ReportItem;
    fileId: string;
  }) {
    const decision = item.decision;
    if (!decision) {
      return Promise.resolve();
    }
    return guarded({
      keys: [item.key],
      action: async () => {
        await withdrawFileMutation.mutateAsync({
          ...mutationScope,
          itemKey: item.key,
          expectedRevision: decision.revision,
          fileId,
        });
      },
    });
  }

  return {
    isBusy: (key: string) => busyKeys.includes(key),
    isAcceptingAll: acceptAllMutation.isPending,
    setStatus,
    saveNote,
    acceptAllValid,
    uploadFile,
    withdrawFile,
  };
}
